import { useState, useEffect, useCallback } from "react";
import { Send, MessageSquare, Bug, Lightbulb, Check, Loader2, Reply, ChevronDown, ChevronUp, Trash2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MSG_TYPES = [
  { value: "suggestion", label: "Suggerimento", icon: Lightbulb, color: "text-amber-600" },
  { value: "bug", label: "Bug / Problema", icon: Bug, color: "text-red-500" },
  { value: "general", label: "Comunicazione", icon: MessageSquare, color: "text-blue-600" },
  { value: "changelog", label: "Aggiornamento", icon: Megaphone, color: "text-violet-600" },
];

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function MessagesPage({ user }) {
  const isTitolare = user?.role === "titolare";
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [type, setType] = useState("suggestion");
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages`);
      setMessages(res.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { text: text.trim(), type });
      toast.success("Messaggio inviato al Titolare");
      setText("");
      fetchMessages();
    } catch {
      toast.error("Errore nell'invio");
    }
    setSending(false);
  };

  const markRead = async (id) => {
    await axios.put(`${API}/messages/${id}/read`);
    setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
  };

  const sendReply = async (msgId) => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      await axios.post(`${API}/messages/${msgId}/reply`, { text: replyText.trim() });
      toast.success("Risposta inviata");
      setReplyText("");
      setReplyingTo(null);
      fetchMessages();
    } catch {
      toast.error("Errore nell'invio della risposta");
    }
    setReplySending(false);
  };

  return (
    <div data-testid="messages-page" className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">
            {isTitolare ? "Messaggi Ricevuti" : "Comunicazioni"}
          </h1>
          <p className="text-xs sm:text-sm text-zinc-500 mt-0.5">
            {isTitolare ? "Suggerimenti, bug e comunicazioni dagli agenti" : "Invia messaggi e leggi le risposte del Titolare"}
          </p>
        </div>
        {isTitolare && messages.length > 0 && (
          <button
            data-testid="clear-messages-btn"
            onClick={async () => {
              if (!window.confirm("Eliminare tutti i messaggi?")) return;
              try {
                await axios.delete(`${API}/messages/all`);
                setMessages([]);
                toast.success("Messaggi eliminati");
              } catch { toast.error("Errore"); }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Svuota tutti
          </button>
        )}
      </div>

      {/* Send form — for agents */}
      {!isTitolare && (
        <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
          <div className="flex gap-1">
            {MSG_TYPES.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.value}
                  onClick={() => setType(t.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold transition-all ${
                    type === t.value ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 hover:text-zinc-700"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
          <textarea
            data-testid="message-input"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Scrivi il tuo messaggio..."
            className="w-full border border-zinc-300 rounded-sm p-3 text-sm resize-none h-24 focus:outline-none focus:ring-1 focus:ring-zinc-400"
          />
          <Button
            data-testid="send-message-btn"
            onClick={sendMessage}
            disabled={sending || !text.trim()}
            className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Invia al Titolare
          </Button>
        </div>
      )}

      {/* Messages list */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 text-zinc-400 text-sm">
            {isTitolare ? "Nessun messaggio ricevuto." : "I tuoi messaggi appariranno qui."}
          </div>
        ) : (
          messages.map(msg => {
            const typeConfig = MSG_TYPES.find(t => t.value === msg.type) || MSG_TYPES[2];
            const Icon = typeConfig.icon;
            const replies = msg.replies || [];
            const isReplying = replyingTo === msg.id;
            return (
              <div
                key={msg.id}
                data-testid={`message-${msg.id}`}
                className={`border rounded-sm transition-all ${
                  !msg.read ? "border-blue-200 bg-blue-50/30" : "border-zinc-200"
                }`}
              >
                <div className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 flex-1">
                      <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${typeConfig.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-xs font-bold text-zinc-700">{msg.from_user}</span>
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-500 border border-zinc-200">
                            {typeConfig.label}
                          </span>
                          {!msg.read && (
                            <span className="text-[9px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-sm">Nuovo</span>
                          )}
                          {replies.length > 0 && (
                            <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-sm">
                              {replies.length} rispost{replies.length === 1 ? "a" : "e"}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-600 whitespace-pre-line">{msg.text}</p>
                        <span className="text-[10px] text-zinc-400 mt-1 block">{formatDate(msg.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isTitolare && !msg.read && (
                        <button
                          onClick={() => markRead(msg.id)}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Letto
                        </button>
                      )}
                      {isTitolare && (
                        <button
                          data-testid={`reply-btn-${msg.id}`}
                          onClick={() => { setReplyingTo(isReplying ? null : msg.id); setReplyText(""); }}
                          className="text-xs text-zinc-500 hover:text-zinc-700 flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-zinc-100 transition-colors"
                        >
                          <Reply className="w-3.5 h-3.5" />
                          Rispondi
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Replies */}
                {replies.length > 0 && (
                  <div className="border-t border-zinc-100 bg-zinc-50/50">
                    {replies.map(r => (
                      <div key={r.id} className="px-3 py-2 flex items-start gap-2 border-b border-zinc-100 last:border-b-0">
                        <Reply className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-bold text-emerald-700">{r.from_user}</span>
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-600 border border-emerald-200">Risposta</span>
                          </div>
                          <p className="text-sm text-zinc-600">{r.text}</p>
                          <span className="text-[10px] text-zinc-400 mt-0.5 block">{formatDate(r.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply form — Titolare only */}
                {isReplying && (
                  <div className="border-t border-zinc-200 p-3 bg-zinc-50">
                    <div className="flex gap-2">
                      <textarea
                        data-testid={`reply-input-${msg.id}`}
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        placeholder={`Rispondi a ${msg.from_user}...`}
                        className="flex-1 border border-zinc-300 rounded-sm p-2.5 text-sm resize-none h-16 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                        autoFocus
                      />
                      <div className="flex flex-col gap-1.5">
                        <Button
                          data-testid={`send-reply-${msg.id}`}
                          onClick={() => sendReply(msg.id)}
                          disabled={replySending || !replyText.trim()}
                          className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm h-8 px-3 text-xs"
                        >
                          {replySending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        </Button>
                        <button
                          onClick={() => { setReplyingTo(null); setReplyText(""); }}
                          className="text-[10px] text-zinc-400 hover:text-zinc-600"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
